import React, { useState, useEffect } from "react";
import Header from "../../../Components/Header";
import Seo from "../../../Components/SeoComponent/Seo";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { verifyGSTIn } from "../../../Services/ApiTestService/apiTestService";
import { InputWithLabel, Button } from "../../../Components/Tags/Tags";
import { FiCheckCircle } from 'react-icons/fi';
import Loader from "../../../Loader";
import { Data4 } from "../Data";
import { Link } from "react-router-dom";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import { useService } from "../../../context/ServiceContext";


const VerifyGST = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [gstData, setGstData] = useState({});
  const [errors, setErrors] = useState({});
  const [profileData, setProfileData] = useState({});
  const [gstNumber, setGstNumber] = useState("");
  const { serviceName } = useService();


  // Fetch profile data on mount
  useEffect(() => { 
    const fetchProfileData = async () => {
      try {
        setIsLoading(true);
        const response = await fetchProfile();
        setProfileData(response.data);
      } catch (error) {
        console.error("Error fetching profile data:", error);
        showErrorToast("Failed to fetch profile data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  // Form validation
  const validateForm = (gstNumber) => {
    const errors = {};

    if (!gstNumber.trim()) {
        errors.gstNumber = "GST is required.";
      } else if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/.test(gstNumber)) {
        errors.gstNumber = "Enter a valid 15-character GST number (e.g., 22ABCDE1234F1Z5).";
      }
      
    return errors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm(gstNumber);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    try {
      setIsLoading(true);

      const response = await verifyGSTIn({
        api_id: profileData?.user_id,
        api_key: profileData?.api_key,
        token_id: profileData?.token_id,
        gstin: gstNumber,
      });

      const statusCode = response?.status?.code || response?.status;

      if (statusCode === 200) {
        const gstData = response?.data;


        // ✅ Check if valid object or array
        if (gstData && (typeof gstData === "object" || Array.isArray(gstData))) {
            setGstData(gstData);
          showSuccessToast(response.status?.message || "GST verified successfully.");

          // Clear form
          setGstNumber("");
          setErrors({});
        } else {
          showErrorToast("GST details are missing or in an unexpected format.");
        }
      } else if (statusCode === 422) {
        const backendErrors = response.data.message || {};

        showErrorToast(response.data.message || "Invalid bank format or data.");
    } else if (statusCode === 500) {
     const errorMessage = response.data.message || response?.message || "Server error occurred";
         showErrorToast(errorMessage);
      }
       else {
        showErrorToast(response.status?.message || response.message || response.data.message || response.error.message || `Something went wrong. (code: ${statusCode})`);
      }     
    } catch (error) {
      showErrorToast("Failed to verify GST details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Seo />
      {isLoading && <Loader />}
      <Header heading={serviceName} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 justify-center md:ml-80 ml-0 p-4">
      
      <div className="col-span-2 md:col-span-6 flex justify-end">
  <Link
    to="/TestMaster"
    className="flex items-center gap-1 text-white bg-green-600 hover:bg-green-700 px-1 py-1 rounded-md font-semibold shadow-md transition duration-300"
  >
    <span>Go Back</span>
    <IoArrowBackCircleOutline size={24} />
  </Link>
</div>

      
        <InputWithLabel
          type="text"
          label="GST No"
          placeholder="Enter GST No"
          maxLength="18"
          className="p-1 rounded shadow-lg shadow-blue-500/50 border border-blue-600"
          name="gstin"
          id="gstin"
          required={true}
          value={gstNumber}
          onChange={(e) => setGstNumber(e.target.value)}
          error={errors.gstNumber}
        />

        <div>
          <Button
            type="button"
            className="text-center text-white bg-blue-900 shadow-lg bg-gradient-to-r from-blue-400 via-blue-1200 to-blue-900 font-semibold rounded-md px-2 py-1 mt-5 text-md hover:from-blue-900 hover:to-blue-400 border-4 ms-2"
            id="verifyGST"
            name="verifyGST"
            text="Verify"
            onClick={handleSubmit}
          />
        </div>
      </div>

      {gstData &&
  typeof gstData === "object" &&
  gstData.pan_number &&
gstData.gstin &&
   (
    <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 px-4">
      {/* Main Details Card */}
      <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-2xl shadow-xl p-6 hover:shadow-2xl transition duration-300">
        <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">
          <FiCheckCircle className="text-blue-500 text-2xl" />
          GST Main Details
        </h2>

        <div className="space-y-2 text-sm text-gray-700">
          <p><span className="font-semibold">Business Name:</span> {gstData?.business_name}</p>
          <p><span className="font-semibold">Legal Name:</span> {gstData?.legal_name}</p>
          <p><span className="font-semibold">GSTIN:</span> {gstData?.gstin}</p>
          <p><span className="font-semibold">PAN:</span> {gstData?.pan_number}</p>
          <p><span className="font-semibold">Constitution:</span> {gstData?.constitution_of_business}</p>
          <p><span className="font-semibold">Type:</span> {gstData?.taxpayer_type}</p>
          <p><span className="font-semibold">Status:</span> {gstData?.gstin_status}</p>
          <p><span className="font-semibold">Registration Date:</span> {gstData?.date_of_registration}</p>
          <p><span className="font-semibold">Business Activity:</span> {gstData?.nature_bus_activities?.join(', ')}</p>
          <p><span className="font-semibold">Core Activity:</span> {gstData?.nature_of_core_business_activity_description}</p>
          <p><span className="font-semibold">Address:</span> {gstData?.address}</p>
        </div>
      </div>

      {/* JSON Preview */}
      <div className="bg-white border border-blue-300 rounded-md shadow-lg">
        <h2 className="text-lg font-semibold text-blue-800 mb-3">Raw GST JSON Data</h2>
        <pre className="bg-gray-100 p-3 rounded text-sm overflow-auto">
          {JSON.stringify(gstData, null, 2)}
        </pre>
      </div>
    </div>
)}


    </>
  );
};

export default VerifyGST;
